import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, Check } from "lucide-react";
import { StatusStepper } from "./StatusStepper";
import { createDraftFromCompany } from "../email-intel/api";

// `services` / `tech_stack` / `pain_points` / `recommended_services` come back
// from the backend as JSON-encoded strings (stored exactly as n8n sends them),
// not parsed objects/arrays - this turns them back into real data client-side.
function safeJsonParse(value, fallback) {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

// `ai_proposal` isn't a fixed shape - n8n's actual output has been seen as
// both a single-item array (`[{ output: {...} }]`) and a bare object
// (`{ id, output: {...} }`), depending on the workflow's exact node history.
// Handle both instead of assuming one - an array-index access on a plain
// object silently returns undefined rather than erroring, which is what
// was making the whole Personalized Email section render as empty.
function extractProposal(ai_proposal) {
  if (!ai_proposal) return null;
  const item = Array.isArray(ai_proposal) ? ai_proposal[0] : ai_proposal;
  return item?.output || item || null;
}

// Which result tabs exist at all depends entirely on what the backend
// actually filled in for this company - only 3 of the 7 workflow-canvas
// stages have any backing data today (see api.js comments). No tab is shown
// for a stage with nothing to display.
const RESULT_TAB_CONFIG = [
  { key: "contact", label: "Contact", hasData: (c) => !!(c?.email || c?.first_name) },
  { key: "research", label: "Company Research", hasData: (c) => !!(c?.industry || c?.match_score != null) },
  { key: "proposal", label: "Personalized Email", hasData: (c) => !!c?.ai_proposal },
];

function resultTabButtonClass(active) {
  return `rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
    active ? "bg-white/20 text-white" : "text-gray-400 hover:bg-white/10 hover:text-gray-200"
  }`;
}

function KeyValue({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-200">{value}</div>
    </div>
  );
}

function TagList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span key={i} className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-gray-300">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function ContactResult({ company }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <KeyValue label="Name" value={[company.first_name, company.last_name].filter(Boolean).join(" ") || null} />
      <KeyValue label="Title" value={company.title} />
      <KeyValue label="Email" value={company.email} />
      <KeyValue label="Organization" value={company.organization_name} />
      <KeyValue label="Domain" value={company.domain} />
      {company.linkedin && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500">LinkedIn</div>
          <a href={company.linkedin} target="_blank" rel="noreferrer" className="text-sm text-blue-400 hover:underline">
            {company.linkedin}
          </a>
        </div>
      )}
    </div>
  );
}

function CompanyResearchResult({ company }) {
  const painPoints = company.pain_points && typeof company.pain_points === "object" ? company.pain_points : {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <KeyValue label="Industry" value={company.industry} />
        <KeyValue label="Business Model" value={company.business_model} />
        <KeyValue label="Priority" value={company.priority} />
        <KeyValue label="Match Score" value={company.match_score != null ? `${company.match_score}/100` : null} />
      </div>
      {company.match_reasoning && (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">Match Reasoning</div>
          <p className="text-sm leading-relaxed text-gray-300">{company.match_reasoning}</p>
        </div>
      )}
      <TagList label="Services" items={company.services} />
      <TagList label="Tech Stack" items={company.tech_stack} />
      <TagList label="Recommended Services" items={company.recommended_services} />
      {Object.entries(painPoints).some(([, items]) => Array.isArray(items) && items.length > 0) && (
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-wide text-gray-500">Pain Points</div>
          <div className="space-y-2">
            {Object.entries(painPoints).map(([category, items]) =>
              Array.isArray(items) && items.length > 0 ? (
                <div key={category}>
                  <div className="mb-1 text-xs font-medium capitalize text-gray-400">{category.replace(/_/g, " ")}</div>
                  <ul className="list-inside list-disc space-y-0.5">
                    {items.map((pt, i) => (
                      <li key={i} className="text-sm text-gray-300">{pt}</li>
                    ))}
                  </ul>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PersonalizedEmailResult({ proposal }) {
  if (!proposal) return <p className="text-sm text-gray-500">No proposal data.</p>;
  return (
    <div className="space-y-4">
      <KeyValue label="Subject" value={proposal.email_subject} />
      {proposal.opening_hook && (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">Opening Hook</div>
          <p className="text-sm italic text-gray-300">{proposal.opening_hook}</p>
        </div>
      )}
      {proposal.email_body && (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">Email Body</div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{proposal.email_body}</p>
        </div>
      )}
      <TagList label="Services Mentioned" items={proposal.services_mentioned} />
      <TagList label="Key Talking Points" items={proposal.keyTalkingPoints} />
      <KeyValue label="Call to Action" value={proposal.callToAction} />
    </div>
  );
}

// Copies this company's generated email into Email Intel as an editable
// draft (POST /email-intel/drafts) - from there the user can send it as-is
// or customize + save it before sending. One-shot per open of this view:
// once it succeeds there's no reason to create a second draft for the same
// proposal, so the button just confirms rather than re-firing.
// `state`/`onSend` are lifted to CompanyResultView (not local useState here)
// because this button is rendered twice - once in the standalone
// "Personalized Email" tab, once inside "All Results" - and only one of
// those is mounted at a time (tab switch unmounts the other), so purely
// local state would reset and risk creating a duplicate draft.
function SendToDraftButton({ state, onSend }) {
  if (state === "sent") {
    return (
      <Button type="button" disabled className="rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
        <Check className="mr-1.5 h-3.5 w-3.5" /> Sent to Draft
      </Button>
    );
  }

  return (
    <Button
      type="button"
      onClick={onSend}
      disabled={state === "sending"}
      className="rounded-lg bg-blue-600 hover:bg-blue-500 text-white"
    >
      {state === "sending" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1.5 h-3.5 w-3.5" />}
      {state === "sending" ? "Sending..." : "Send to Draft"}
    </Button>
  );
}

function AllResultsView({ company, draftState, onSendToDraft }) {
  const proposal = extractProposal(company.ai_proposal);
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Contact</div>
        <ContactResult company={company} />
      </div>
      {(company.industry || company.match_score != null) && (
        <div className="border-t border-white/10 pt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Company Research</div>
          <CompanyResearchResult company={company} />
        </div>
      )}
      {company.ai_proposal && (
        <div className="border-t border-white/10 pt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Personalized Email</div>
            <SendToDraftButton state={draftState} onSend={onSendToDraft} />
          </div>
          <PersonalizedEmailResult proposal={proposal} />
        </div>
      )}
    </div>
  );
}

// The one component either the trigger form (Startworlflow.jsx, right after
// its own run finishes) or a history list (LeadEngine.jsx, on clicking any
// past company) should render - same "All Results" + per-stage tabs UI
// either way, so a company looks identical regardless of how you got to it.
// Pass the raw company object straight from GET /lead-engine/companies/{id}
// or a GET /lead-engine/companies list item - the JSON-string fields get
// parsed internally.
export function CompanyResultView({ company, className = "" }) {
  const [tab, setTab] = useState("all");
  // Seeded from the backend's own send_to_draft flag (GET /lead-engine/
  // companies[/{id}]) rather than always starting at "idle" - otherwise
  // re-opening a company that already has a draft (e.g. from history) would
  // show "Send to Draft" again and let a duplicate draft be created for the
  // same proposal. Re-synced on company change since this view can be
  // reused for a different company without unmounting (see the effect
  // below).
  const [draftState, setDraftState] = useState(company?.send_to_draft ? "sent" : "idle"); // idle | sending | sent | error

  useEffect(() => {
    setDraftState(company?.send_to_draft ? "sent" : "idle");
  }, [company?.id, company?.send_to_draft]);

  if (!company) return null;

  const parsed = {
    ...company,
    services: safeJsonParse(company.services, []),
    tech_stack: safeJsonParse(company.tech_stack, []),
    pain_points: safeJsonParse(company.pain_points, {}),
    recommended_services: safeJsonParse(company.recommended_services, []),
  };

  const handleSendToDraft = async () => {
    setDraftState("sending");
    try {
      await createDraftFromCompany(parsed.id);
      setDraftState("sent");
      toast.success("Sent to Draft", { description: "Find it under Email Intel → Inbox to review or customize before sending." });
    } catch (err) {
      console.error("Failed to create email draft", err);
      setDraftState("error");
      toast.error("Could not send to draft", { description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <div className={`rounded-2xl border border-white/20 bg-transparent backdrop-blur-md ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 p-3">
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setTab("all")} className={resultTabButtonClass(tab === "all")}>
            All Results
          </button>
          {RESULT_TAB_CONFIG.filter((t) => t.hasData(parsed)).map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} className={resultTabButtonClass(tab === t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <StatusStepper status={parsed.status} />
          <span className="pr-1 text-xs font-medium text-white truncate max-w-[160px]">{parsed.company_name}</span>
        </div>
      </div>
      <div className="p-5">
        {tab === "contact" && <ContactResult company={parsed} />}
        {tab === "research" && <CompanyResearchResult company={parsed} />}
        {tab === "proposal" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <SendToDraftButton state={draftState} onSend={handleSendToDraft} />
            </div>
            <PersonalizedEmailResult proposal={extractProposal(parsed.ai_proposal)} />
          </div>
        )}
        {tab === "all" && <AllResultsView company={parsed} draftState={draftState} onSendToDraft={handleSendToDraft} />}
      </div>
    </div>
  );
}

export default CompanyResultView;
